import React, { useMemo, useState } from 'react';
import { MenuCategory } from '../../types/api';
import {
  CategoryNode,
  allowedParents,
  branchItemsCount,
  buildCategoryTree,
  categoryPathLabel,
} from '../../utils/categoryTree';
import { MAX_CATEGORY_DEPTH } from '../../config/constants';

/**
 * Панель разделов каталога.
 *
 * Разделы — дерево, товары лежат только в конечных. Поэтому у ветки вместо
 * счётчика стоит подпись «раздел»: партнёр не должен искать в ней позиции и
 * считать пустоту ошибкой.
 *
 * Компонент только показывает и зовёт колбэки — запросы и обработка ошибок
 * остаются на странице каталога.
 */
interface CategoryTreeProps {
  categories: MenuCategory[];
  selectedId: number | 'all';
  totalItems: number;
  onSelect: (id: number | 'all') => void;
  onCreate: (name: string, parentId: number | null) => Promise<void>;
  /** Разбить наполненный раздел: подраздел заводится вместе с переносом товаров. */
  onSplit: (category: MenuCategory, name: string) => Promise<void>;
  onSave: (category: MenuCategory, name: string, parentId: number | null) => Promise<void>;
  onDelete: (category: MenuCategory, node: CategoryNode) => Promise<void>;
}

type Draft =
  | { mode: 'create'; parentId: number | null }
  // Разбиение: в разделе уже лежат товары, и они переедут в новый подраздел.
  | { mode: 'split'; category: MenuCategory }
  | { mode: 'edit'; category: MenuCategory }
  | null;

const CaretIcon: React.FC = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" aria-hidden="true">
    <path d="M9 6l6 6-6 6" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const PlusIcon: React.FC = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" aria-hidden="true">
    <path d="M12 5v14M5 12h14" strokeLinecap="round" />
  </svg>
);

const PencilIcon: React.FC = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
    <path d="M4 20h4l10-10-4-4L4 16v4z" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const TrashIcon: React.FC = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
    <path d="M5 7h14M10 7V5h4v2M7 7l1 12h8l1-12" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

export const CategoryTree: React.FC<CategoryTreeProps> = ({
  categories,
  selectedId,
  totalItems,
  onSelect,
  onCreate,
  onSplit,
  onSave,
  onDelete,
}) => {
  const tree = useMemo(() => buildCategoryTree(categories), [categories]);
  const [collapsed, setCollapsed] = useState<number[]>([]);
  const [draft, setDraft] = useState<Draft>(null);
  const [name, setName] = useState('');
  const [parentId, setParentId] = useState<number | null>(null);
  const [isSaving, setIsSaving] = useState(false);

  const openCreate = (parent: number | null) => {
    setDraft({ mode: 'create', parentId: parent });
    setName('');
    setParentId(parent);
    setCollapsed((prev) => prev.filter((id) => id !== parent));
  };

  const openSplit = (category: MenuCategory) => {
    setDraft({ mode: 'split', category });
    setName('');
    setParentId(category.id);
  };

  const openEdit = (category: MenuCategory) => {
    setDraft({ mode: 'edit', category });
    setName(category.name);
    setParentId(category.parent_id);
  };

  const closeDraft = () => {
    setDraft(null);
    setName('');
    setParentId(null);
  };

  const submit = async (event: React.FormEvent) => {
    event.preventDefault();
    const trimmed = name.trim();
    if (!trimmed || !draft) return;

    try {
      setIsSaving(true);
      if (draft.mode === 'create') {
        await onCreate(trimmed, parentId);
      } else if (draft.mode === 'split') {
        await onSplit(draft.category, trimmed);
      } else {
        await onSave(draft.category, trimmed, parentId);
      }
      closeDraft();
    } finally {
      setIsSaving(false);
    }
  };

  /** Выпадающий список родителей: пусто — верхний уровень. */
  const parentSelect = (moving?: MenuCategory) => (
    <select
      className="qp-select"
      value={parentId ?? ''}
      onChange={(event) => setParentId(event.target.value ? Number(event.target.value) : null)}
    >
      <option value="">Верхний уровень</option>
      {allowedParents(categories, moving, MAX_CATEGORY_DEPTH).map((candidate) => (
        <option key={candidate.id} value={candidate.id}>
          {categoryPathLabel(candidate, categories)}
        </option>
      ))}
    </select>
  );

  const draftForm = (moving?: MenuCategory) => (
    <form className="qp-cats-form" onSubmit={submit}>
      <input
        className="qp-input"
        value={name}
        autoFocus
        placeholder="Например: Безалкогольные"
        onChange={(event) => setName(event.target.value)}
      />
      {parentSelect(moving)}
      <button type="submit" className="qp-btn qp-btn--primary qp-btn--sm" disabled={isSaving || !name.trim()}>
        {isSaving ? 'Сохраняем…' : 'Сохранить'}
      </button>
      <button type="button" className="qp-btn qp-btn--ghost qp-btn--sm" onClick={closeDraft}>
        Отмена
      </button>
    </form>
  );

  /**
   * Разбиение: имя подраздела, в который уедут текущие товары раздела.
   *
   * Родителя тут не выбирают — он и есть разбиваемый раздел.
   */
  const splitForm = (category: MenuCategory) => (
    <form className="qp-cats-form" onSubmit={submit}>
      <p className="qp-cell-sub" style={{ flexBasis: '100%', margin: 0 }}>
        Товаров в разделе: {category.menu_items_count ?? 0}. Они переедут в новый подраздел —
        каталог не потеряет ни одной позиции, а акции на «{category.name}» продолжат действовать.
      </p>
      <input
        className="qp-input"
        value={name}
        autoFocus
        placeholder="Например: Основное меню"
        onChange={(event) => setName(event.target.value)}
      />
      <button type="submit" className="qp-btn qp-btn--primary qp-btn--sm" disabled={isSaving || !name.trim()}>
        {isSaving ? 'Переносим…' : 'Разбить'}
      </button>
      <button type="button" className="qp-btn qp-btn--ghost qp-btn--sm" onClick={closeDraft}>
        Отмена
      </button>
    </form>
  );

  const renderNode = (node: CategoryNode): React.ReactNode => {
    const isEditing = draft?.mode === 'edit' && draft.category.id === node.id;
    const isOpen = !collapsed.includes(node.id);
    const hasChildren = node.children.length > 0;
    const ownItems = node.menu_items_count ?? 0;

    return (
      <React.Fragment key={node.id}>
        {isEditing ? (
          draftForm(node)
        ) : (
          <div
            className={[
              'qp-cats-row',
              selectedId === node.id ? 'qp-cats-row--active' : '',
              node.depth === 0 ? 'qp-cats-row--root' : '',
            ]
              .filter(Boolean)
              .join(' ')}
            style={{ marginLeft: node.depth * 14 }}
          >
            {hasChildren ? (
              <button
                type="button"
                className="qp-cats-twig"
                aria-expanded={isOpen}
                aria-label={isOpen ? 'Свернуть' : 'Развернуть'}
                onClick={() =>
                  setCollapsed((prev) =>
                    prev.includes(node.id) ? prev.filter((id) => id !== node.id) : [...prev, node.id],
                  )
                }
              >
                <CaretIcon />
              </button>
            ) : (
              <span className="qp-cats-twig" aria-hidden="true" />
            )}

            <button type="button" className="qp-cats-name" onClick={() => onSelect(node.id)}>
              <span className="qp-cats-title">{node.name}</span>
              {hasChildren ? (
                // У ветки свой счётчик бессмыслен — товаров в ней нет по
                // правилу каталога. Показываем, сколько их во всей ветке.
                <span className="qp-chip-count">{branchItemsCount(node)}</span>
              ) : (
                <span className="qp-chip-count">{ownItems}</span>
              )}
              {hasChildren && <span className="qp-cats-branch">раздел</span>}
            </button>

            <span className="qp-cats-actions">
              {/* Глубже предела подраздел не заводится — кнопку прячем, а не
                  показываем ошибку после нажатия. В разделе с товарами та же
                  кнопка разбивает его: позиции переезжают в подраздел, потому
                  что рядом с подразделами им места нет. */}
              {node.depth + 2 <= MAX_CATEGORY_DEPTH && (
                <button
                  type="button"
                  className="qp-cats-act"
                  title={ownItems === 0 ? 'Добавить подраздел' : 'Разбить на подразделы'}
                  onClick={() => (ownItems === 0 ? openCreate(node.id) : openSplit(node))}
                >
                  <PlusIcon />
                </button>
              )}
              <button type="button" className="qp-cats-act" title="Переименовать или перенести" onClick={() => openEdit(node)}>
                <PencilIcon />
              </button>
              <button
                type="button"
                className="qp-cats-act qp-cats-act--danger"
                title="Удалить раздел"
                onClick={() => onDelete(node, node)}
              >
                <TrashIcon />
              </button>
            </span>
          </div>
        )}

        {draft?.mode === 'create' && draft.parentId === node.id && (
          <div style={{ marginLeft: (node.depth + 1) * 14 }}>{draftForm()}</div>
        )}

        {draft?.mode === 'split' && draft.category.id === node.id && (
          <div style={{ marginLeft: (node.depth + 1) * 14 }}>{splitForm(draft.category)}</div>
        )}

        {isOpen && node.children.map(renderNode)}
      </React.Fragment>
    );
  };

  return (
    <div className="qp-panel">
      <div className="qp-panel-head">
        <h2 className="qp-label">Разделы</h2>
        <button type="button" className="qp-btn qp-btn--outline qp-btn--sm" onClick={() => openCreate(null)}>
          + Раздел
        </button>
      </div>

      <div className="qp-cats">
        <div className={`qp-cats-row${selectedId === 'all' ? ' qp-cats-row--active' : ''}`}>
          <span className="qp-cats-twig" aria-hidden="true" />
          <button type="button" className="qp-cats-name" onClick={() => onSelect('all')}>
            <span className="qp-cats-title">Все позиции</span>
            <span className="qp-chip-count">{totalItems}</span>
          </button>
        </div>

        {draft?.mode === 'create' && draft.parentId === null && draftForm()}

        {tree.map(renderNode)}
      </div>
    </div>
  );
};
